import { useState, useEffect, useRef } from "react";
import { Bell, CheckCircle, AlertTriangle, Info, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const typeIcons = { success: CheckCircle, warning: AlertTriangle, info: Info };
const typeColors = { success: "text-emerald-600 bg-emerald-50", warning: "text-amber-600 bg-amber-50", info: "text-blue-600 bg-blue-50" };

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (user) fetchNotifs();
    const interval = setInterval(() => { if (user) fetchNotifs(); }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchNotifs = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { data } = await axios.get(`${API}/notifications`,  { headers: { Authorization: `Bearer ${token}` } });
      setNotifs(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {}
  };

  const markRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/notifications/read`, {},  { headers: { Authorization: `Bearer ${token}` } });
      setUnread(0);
      setNotifs(n => n.map(x => ({ ...x, read: true })));
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(!open); if (!open && unread > 0) markRead(); }}
        data-testid="notification-bell" className="relative p-2 text-slate-500 hover:text-blue-600 transition-colors">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="p-8 text-center"><Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-400">No notifications yet</p></div>
            ) : (
              notifs.map(n => {
                const Icon = typeIcons[n.type] || Info;
                const color = typeColors[n.type] || typeColors.info;
                return (
                  <div key={n.id} className={`p-3 border-b border-slate-50 last:border-0 ${!n.read ? "bg-blue-50/30" : ""}`}>
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${color}`}><Icon className="w-4 h-4" /></div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{n.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
